import dayjs from 'dayjs'
import {
  DripModeInfo,
  EmailScheduleStatus,
  IScheduleIntialOptions,
  ISchedule,
  ScheduleStatus,
  recordListSchedule,
  ICustomField,
  ICustomValue,
} from '../types'
import i18n from 'common/locales'
import {
  PAGE_LIMIT,
  WEEK_DAYS,
  scheduleTimeSlots,
  supportedAttachmentTypes,
} from './const_helper'
import handlebars from 'handlebars'
import { getTagOptions } from './../config'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { isURL } from 'validator'
// for importing Do format
dayjs.extend(advancedFormat)
dayjs().format('Do')

export function hasHandleBar(value: any = '') {
  const replacedBars = value.replaceAll('{', '').replaceAll('}', '')
  return replacedBars !== value
}

export function isValidHandleBars(value: string) {
  const regex =
    /((^{{|[^{\n\r]{{)(?!{))(?!( ?(\w\.?)+ ?(}}(?!}))))|((^{{{|[^{\n\r]{{{)(?!{))(?!( ?(\w\.?)+ ?(}}}(?!}))))|{{4,}|^{(?= )/m

  return !regex.test(value) ? true : false
}

export function getTimeSlotsSelectOptions(
  isSameDay = false,
  selectedDate: string
) {
  const selectedOnlyDate = selectedDate.split(' ')[0]
  const slots = scheduleTimeSlots
    .map(x => {
      if (isSameDay) {
        if (
          dayjs(`${selectedOnlyDate} ${x}`, 'YYYY-MM-DD HH:mm A').isAfter(
            dayjs(`${selectedDate}`)
          )
        ) {
          return {
            value: x,
            label: x,
          }
        }
        return null
      }
      return {
        value: x,
        label: x,
      }
    })
    .filter(x => x)
  return slots
}

export const getTimeZoneNameRelativeToUTC = function (
  timeZone: String = dayjs.tz.guess()
) {
  // @ts-ignore
  return `GMT${dayjs().tz(timeZone).format('Z')} ${timeZone}`
}

export function debounce(func: any, wait: number, immediate?: any) {
  let timeout: any
  return function () {
    // @ts-ignore
    var context = this,
      args = arguments
    var later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    var callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export const getEmailServiceHeader = function (channel = 'APP') {
  return {
    channel: channel,
    source: 'WEB_USER',
    version: '2021-07-28',
  }
}

export const getPercentage = function (
  value: number,
  total: number,
  fixed = 2
) {
  return Number(value) && Number(total)
    ? ((value / total) * 100).toFixed(fixed)
    : 0
}

export const isCampaignProcessing = function (campaign: ISchedule) {
  const { status, emailStatus, type } = campaign || {}
  const invalidTypes = ['folder', 'rss', 'abtest']
  const processingStatuses = [
    EmailScheduleStatus.QUEUEING,
    EmailScheduleStatus.PROCESSING,
    EmailScheduleStatus.WAITING,
    EmailScheduleStatus.READING,
    EmailScheduleStatus.SCHEDULED,
  ]

  return (
    status === ScheduleStatus.ACTIVE &&
    (!emailStatus ||
      processingStatuses.includes(emailStatus as EmailScheduleStatus) ||
      campaign.emailStatus?.includes(EmailScheduleStatus.READING)) &&
    !invalidTypes.includes(type)
  )
}

export const getExecutionErrorMessage = function (item = {} as ISchedule) {
  let failed = false
  let lastExecutionStatus = item.lastExecutionStatus || ''
  const lowerCaseStatus = lastExecutionStatus.toLowerCase()
  if (item?.lastExecutionStatus && item.status === ScheduleStatus.PAUSE) {
    //cases where there are failure from the bulk action service
    //like can not have more than 5 concurrent request
    if (
      !lowerCaseStatus.includes(
        'no new post found for the campaign in last execution'
      )
    )
      failed = true
  }
  if (
    item.status === ScheduleStatus.CANCELLED ||
    item.emailStatus === EmailScheduleStatus.CANCELLED
  ) {
    failed = true
    lastExecutionStatus = 'Campaign has been cancelled'
  }
  if (item?.type !== 'abtest') {
    failed = !failed
      ? item.totalCount > 0 && item.failed === item.totalCount
      : failed
  }

  const statusInfo = { isError: failed, executionStatus: lastExecutionStatus }
  try {
    if (lowerCaseStatus.includes('maximum retry exhaust:')) {
      const colanIndex = lastExecutionStatus.indexOf(':')
      statusInfo.isError = true
      statusInfo.executionStatus = i18n.global.t(
        'campaignSchedule.maximumAttemptsHelper',
        { reason: lastExecutionStatus.substring(colanIndex) }
      )
    }
    if (
      lowerCaseStatus.includes(
        'no new post found for the campaign in last execution'
      )
    ) {
      statusInfo.executionStatus = i18n.global.t(
        'campaignSchedule.noNewPostFound'
      )
    }
    if (
      lowerCaseStatus.includes(
        'fail to schedule the next execution for the campaign,'
      )
    ) {
      const colanIndex = lowerCaseStatus.indexOf(',')
      statusInfo.executionStatus = i18n.global.t(
        'campaignSchedule.rssCampaignFailed',
        { reason: lastExecutionStatus.substring(colanIndex) }
      )
      statusInfo.isError = true
    }
    if (
      lowerCaseStatus.includes(
        'fail to schedule the winning variation for the a/b campaign,'
      )
    ) {
      const colanIndex = lowerCaseStatus.indexOf(',')
      statusInfo.executionStatus = i18n.global.t(
        'campaignSchedule.winningVariationFailed',
        { reason: lastExecutionStatus.substring(colanIndex) }
      )
      statusInfo.isError = true
    }
    if (lowerCaseStatus.includes('fail to create bulk request')) {
      statusInfo.isError = true
      statusInfo.executionStatus = i18n.global.t(
        'campaignSchedule.bulkRequestFailed'
      )
    }
    if (lowerCaseStatus.includes('campaign is not scheduled to be send on')) {
      statusInfo.isError = false
      statusInfo.executionStatus = lowerCaseStatus.replace(
        'campaign is not scheduled to be send on',
        'Scheduled Email Campaign has failed to send on'
      )
    }
    if (item.status === ScheduleStatus.COMPLETE) {
      statusInfo.executionStatus = ''
    }
  } catch (err) {
    console.warn(err)
  }

  return statusInfo
}

// https://github.com/UrbanCompass/to-start-case
export const titleCase = function (str: string) {
  return str
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, (str, $1, $2) => $1 + ' ' + $2)
    .replace(/(\s|^)(\w)/g, (str, $1, $2) => $1 + $2.toUpperCase())
}

export function getRequestOffset({
  offset = '',
  resetState = false,
  nextPage = true,
  currentPageCount = 1,
  pageNumber = 0,
}) {
  if (offset) return offset
  else if (resetState) return '0'
  else if (nextPage) return String(currentPageCount * pageNumber)
  else return String((pageNumber - 1) * PAGE_LIMIT - PAGE_LIMIT)
}

export const AB_VARIATION_COLOR_TYPE: any = {
  0: 'primary',
  1: 'warning',
  2: 'error',
  3: 'success',
  4: 'warning',
  5: 'default',
  6: 'primary',
  7: 'warning',
  8: 'error',
}

export const EMAIL_STATUS_TYPE_COLOR: any = {
  0: '#2970FF',
  1: '#F79009',
  2: '#12B76A',
  3: '#4E5BA6',
  4: '#875BF7',
  5: '#EE46BC',
  6: '#7B2D26',
  7: '#F04438',
  8: '#669F2A',
}

export function getABVariations(count = 6, start = 0) {
  const variationsOptions = [
    {
      label: '1',
      value: 1,
      charLabel: 'a',
      colorType: AB_VARIATION_COLOR_TYPE[0],
    },
    {
      label: '2',
      value: 2,
      charLabel: 'b',
      colorType: AB_VARIATION_COLOR_TYPE[1],
    },
    {
      label: '3',
      value: 3,
      charLabel: 'c',
      colorType: AB_VARIATION_COLOR_TYPE[2],
    },
    {
      label: '4',
      value: 4,
      charLabel: 'd',
      colorType: AB_VARIATION_COLOR_TYPE[3],
    },
    {
      label: '5',
      value: 5,
      charLabel: 'e',
      colorType: AB_VARIATION_COLOR_TYPE[4],
    },
    {
      label: '6',
      value: 6,
      charLabel: 'f',
      colorType: AB_VARIATION_COLOR_TYPE[5],
    },
  ]
  variationsOptions.splice(0, start)
  if (count === 6) return variationsOptions

  variationsOptions.splice(count, 6 - count)

  return variationsOptions
}

export function getCharABVariations(count = 6) {
  const variationsOptions = [
    {
      label: 'A',
      value: 'a',
      colorType: AB_VARIATION_COLOR_TYPE[0],
    },
    {
      label: 'B',
      value: 'b',
      colorType: AB_VARIATION_COLOR_TYPE[1],
    },
    {
      label: 'C',
      value: 'c',
      colorType: AB_VARIATION_COLOR_TYPE[2],
    },
    {
      label: 'D',
      value: 'd',
      colorType: AB_VARIATION_COLOR_TYPE[3],
    },
    {
      label: 'E',
      value: 'e',
      colorType: AB_VARIATION_COLOR_TYPE[4],
    },
    {
      label: 'F',
      value: 'f',
      colorType: AB_VARIATION_COLOR_TYPE[5],
    },
  ]
  if (count === 6) return variationsOptions
  variationsOptions.splice(count, 6 - count)
  return variationsOptions
}

export function nextChar(c, incrementCount = 1) {
  return String.fromCharCode(c.charCodeAt(0) + incrementCount)
}

export const formatFileSize = (bytes: number) => {
  const KB = 1024
  const MB = 1024 * KB

  if (bytes < KB) {
    return bytes + ' bytes'
  } else if (bytes < MB) {
    return (bytes / KB).toFixed(2) + ' KB'
  } else {
    return (bytes / MB).toFixed(2) + ' MB'
  }
}

export const isFileSupported = file => {
  const isMimeTypeSupported =
    file?.type &&
    supportedAttachmentTypes.some(type => type.mimeType === file.type)
  const isExtensionSupported =
    !file?.type &&
    file?.name &&
    supportedAttachmentTypes
      .flatMap(type => type.extensions)
      .includes(`.${file.name.split('.').pop()}`)

  if (!isMimeTypeSupported && !isExtensionSupported) {
    return false
  }
  return true
}

export function getInitialScheduleOptions(
  campaign: ISchedule
): IScheduleIntialOptions {
  if (!campaign) return {} as IScheduleIntialOptions
  // destructure campaign
  const {
    meta: {
      bulkReqInfo: {
        opSpecs: {
          subject: bulkSubject = '',
          from: bulkFrom = '',
          replyToAddress: bulkReplyTo = '',
        } = {},
        recordListSchedule = {} as recordListSchedule,
        sprintSchedule = {} as any,
      } = {},
      rssInfo: {
        subject: rssSubject = '',
        from: rssFrom = '',
        replyToAddress: rssReplyTo = '',
      } = {},
      previewText,
    } = {},
  } = campaign

  const from = bulkFrom || rssFrom
  const replyToAddress = bulkReplyTo || rssReplyTo
  const trimmedFrom = from.trim()
  const lastAngularIndex = trimmedFrom.search(/([^<]*$)/i)
  const fromName =
    lastAngularIndex > 0 ? trimmedFrom.substring(0, lastAngularIndex - 1) : ''
  const fromAddress =
    lastAngularIndex > 0
      ? trimmedFrom.substring(lastAngularIndex).replace('>', '')
      : ''
  let scheduleStartDate = ''
  let scheduleStartTime = ''
  let dripModeData: DripModeInfo = {} as DripModeInfo

  if (recordListSchedule?.scheduleStart) {
    const [startDate, startTime, AmPm] =
      recordListSchedule.scheduleStart.split(' ')
    scheduleStartDate = startDate
    scheduleStartTime = `${startTime} ${AmPm ?? ''}`
  }

  if (sprintSchedule?.scheduleStart) {
    const [startDate, startTime, AmPm] = sprintSchedule.scheduleStart.split(' ')
    scheduleStartDate = startDate
    scheduleStartTime = `${startTime} ${AmPm ?? ''}`
  }

  if (sprintSchedule?.sprintLimit || sprintSchedule?.sprintDelayType) {
    const weekDays = [...WEEK_DAYS]
    const {
      scheduleStart,
      sprintDelayType: selectedAfterUnit,
      processWindowStart: startFromTime,
      processWindowEnd: endAtTime,
      sprintLimit: batchQuantity,
      sprintDelay: repeatAfter,
      skipDays = [],
    } = sprintSchedule
    const sendDays = weekDays.filter(day => !skipDays.includes(day))

    dripModeData = {
      startOnDate: scheduleStart,
      selectedAfterUnit,
      startFromTime,
      endAtTime,
      batchQuantity: batchQuantity,
      repeatAfter: repeatAfter,
      sendDays,
    }
  }

  return {
    subject: bulkSubject || rssSubject,
    fromAddress,
    fromName,
    scheduleStartDate,
    scheduleStartTime,
    dripModeData,
    previewText,
    replyToAddress,
  }
}

export const transformMenuOption = (arr: any) => {
  if (!arr) return null
  return arr.map(({ text, menu, value = text }: any) => ({
    label: text,
    value: value,
    children: menu ? transformMenuOption(menu) : undefined,
  }))
}

export const convertMenuOptions = (arr: any) => {
  if (!arr) return []
  return arr.map(({ label, value = label, children }: any) => ({
    text: label,
    value,
    menu: children && convertMenuOptions(children),
  }))
}

// hext to rgba
export const hexToRGBA = (hex: string): string => {
  let r: number = 0,
    g: number = 0,
    b: number = 0,
    a: number | string = 1

  hex = hex.replace('#', '')
  if (hex.length === 3 || hex.length === 4) {
    // convert to 6 digit
    hex = hex
      .split('')
      .map(s => s + s)
      .join('')
  }

  if (hex.length === 6) {
    ;[r, g, b] = (hex.match(/.{1,2}/g) || []).map(val => parseInt(val, 16))
    a = 1
  } else if (hex.length === 8) {
    ;[r, g, b, a] = (hex.match(/.{1,2}/g) || []).map(val => parseInt(val, 16))
    a = (a / 255).toFixed(2)
  } else {
    return hex
  }

  return `rgba(${r}, ${g}, ${b}, ${a})`
}

// check if it is a rgba
export const isRGBA = (color: string): boolean => {
  // Regular expression to match rgba color in the format of rgba(r, g, b, a)
  const rgbaRegex =
    /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(0(\.\d{1,2})?|1(\.0{1,2})?)\s*\)$/

  return rgbaRegex.test(color)
}

// rgba to hex color
export const rgbaToHex = (color: string): string => {
  const rgbaValues = color
    .substring(5, color.length - 1)
    .split(',')
    .map(val => parseFloat(val.trim()))
  const [r, g, b, a] = rgbaValues
  const alphaHex = Math.round(a * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase()

  const rgbHex = [r, g, b].map(val => {
    const hexVal = Math.round(val).toString(16).toUpperCase()
    return hexVal.length === 1 ? '0' + hexVal : hexVal
  })

  return `#${rgbHex.join('')}${alphaHex}`
}

interface Item {
  id: string
  name: string
  fieldKey: string
  value?: any
}

export const convertToKeyValue = (payload: any, key?: string, value?: string) =>
  Object.fromEntries(
    payload?.map((obj: any) => [
      obj[key ?? 'fieldKey'],
      value === 'meta'
        ? obj[value ?? 'meta']['value'] ?? ''
        : obj[value ?? 'value'] ?? '',
    ])
  )

export const compileCustomValue = (
  value: string,
  data: { [key: string]: any }
): string => {
  try {
    type NestedObject = {
      [key: string]: string | NestedObject
    }
    const nestedObject: NestedObject = {}

    for (const key in data) {
      const keyParts = key.replace(/[{}]/g, '').split('.') // Extract nested keys
      let nested: NestedObject = nestedObject

      for (let i = 0; i < keyParts.length - 1; i++) {
        const currentKey = keyParts[i]?.trim()
        nested[currentKey] = nested[currentKey] || {}
        nested = nested[currentKey] as NestedObject
      }

      nested[keyParts[keyParts.length - 1]?.trim()] = data[key]
    }
    return handlebars.compile(value)(nestedObject)
  } catch (error) {
    return value
  }
}

function flattenCustomData(arr: ICustomValue[]) {
  return arr.flatMap(field => {
    if (field.children && field.children.length > 0) {
      return flattenCustomData(field.children)
    } else {
      return field.meta?.documentType !== 'folder' ? [field] : []
    }
  })
}

export const getHandleBarCustomValues = async (
  url: string,
  state: any,
  contactData?: any
) => {
  try {
    const response = await fetch(url)
    if (response.ok) {
      const htmlString = await response.text()
      let defaultTags: any = []
      const tagOptions = getTagOptions()

      // convert default tags
      for (let i = 1; i < tagOptions.length; i++) {
        const item = tagOptions[i]
        defaultTags = {
          ...defaultTags,
          ...convertToKeyValue(
            item.menu.filter(val => val.value),
            'value',
            'value'
          ),
        }
      }

      // contacts custom values
      if (contactData) {
        const { address_1, contact_name, custom_fields } = contactData
        let updatedFields = []
        if (state?.customFieldsLocation) {
          updatedFields = state?.customFieldsLocation?.map(
            (val: ICustomField) => {
              const field = custom_fields?.find(({ id }) => val.id === id)
              if (field) {
                if (field.type === 'string') {
                  return {
                    fieldKey: val?.fieldKey.split('.')[1],
                    value: field['field_value_string'],
                  }
                } else if (field.type === 'number') {
                  return {
                    fieldKey: val?.fieldKey.split('.')[1],
                    value: field['field_value_number'],
                  }
                } else if (field.type === 'array') {
                  return {
                    fieldKey: val?.fieldKey.split('.')[1],
                    value: field['field_value_array'],
                  }
                }
              }
              return { fieldKey: val?.fieldKey.split('.')[1], value: '' }
            }
          )
        }

        const contactTags = {
          contact: {
            ...contactData,
            full_address: address_1,
            address1: address_1,
            name: contact_name,
            full_name: contact_name,
            ...convertToKeyValue(updatedFields),
          },
        }

        defaultTags = { ...defaultTags, ...contactTags }
      }

      // convert custom values
      if (state?.customValuesLocation)
        defaultTags = {
          ...defaultTags,
          ...convertToKeyValue(
            flattenCustomData(state?.customValuesLocation),
            'value',
            'meta'
          ),
        }

      // trigger links
      if (state?.triggerLinksLocation)
        defaultTags = {
          ...defaultTags,
          ...convertToKeyValue(
            state?.triggerLinksLocation,
            'fieldKey',
            'fieldKey'
          ),
        }

      return compileCustomValue(htmlString, defaultTags)
    } else {
      throw new Error('Failed to fetch the HTML content')
    }
  } catch (error) {
    console.error('Error while fetching template:', error)
  }
}

export const getProfileColor = (customerName: string) => {
  let str = customerName
  var hash = 0
  if (!str || str.length == 0) return hash
  for (var i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
    hash = hash & hash // Convert to 32bit integer
  }
  var shortened = Math.abs(hash) % 360
  return 'hsl(' + shortened + ',35%, 60%)'
}
export const getInitials = (customerName: string): string => {
  if (!customerName) return '?'
  const parts = customerName.split(' ')
  let initials = parts[0].substring(0, 1).toUpperCase()

  if (parts.length > 1) {
    initials += parts[parts.length - 1].substring(0, 1).toUpperCase()
  }
  return initials
}

export const getEmailDate = (val: number) => dayjs(val).format('MMM Do, YYYY')

export const isValidHyperLink = (val: string) => {
  let value = ''
  if (!isValidHandleBars(val)) return false
  try {
    value = handlebars.compile(val)({}) // remove handlebars
  } catch (error) {
    return false
  }
  if (!value) return true // ignore if value is empty

  const telRegex = /^tel:[+]?[0-9]+$/
  const mailtoRegex = /^mailto:[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/

  if (value.indexOf('mailto:') === 0) return mailtoRegex.test(value)
  else if (value.indexOf('tel:') === 0) return telRegex.test(value)
  else return isURL(value, { require_protocol: true })
}
